import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject } from 'rxjs';
import { ProgressData } from '../../../../shared/dtos/progress-data';
import { ProgressIndicatingService } from '../../../../shared/services/progress-indicating.service';
import { UserCreateDto, UserCUDto } from '../../dtos/internal-user.dto';
import { RoleDto } from '../../dtos/role.dto';
import { UserTypes } from '../../enums/user-type.enum';
import { InternalUsersService } from '../../services/internal-users.service';

@Component({
    selector: 'app-update-user-dialog',
    templateUrl: './update-user-dialog.component.html',
    styleUrls: ['./update-user-dialog.component.scss']
})
export class UpdateUserDialogComponent implements OnInit {
    public userForm: FormGroup;
    public userTypes = Object.values(UserTypes);
    public roles: RoleDto[] = [];
    public selectedRole: RoleDto;
    private user: UserCUDto;
    private progress$ = new Subject<ProgressData>();
    private unsubscribe$ = new Subject<void>();
    public constructor(
        public ref: DynamicDialogRef,
        config: DynamicDialogConfig,
        private fb: FormBuilder,
        private userService: InternalUsersService,
        private messageService: MessageService,
        private progressIndicatingService: ProgressIndicatingService) {
        this.roles = config.data.roles;
        this.user = config.data.user;
        if (this.user) {
            this.selectedRole = {} as RoleDto;
            const roles = this.user.roles;
            if (!!roles && roles.length > 0) {
                this.selectedRole = roles[0];
            }
            this.userForm = this.fb.group({
                firstName: [this.user.firstName, [Validators.required, Validators.pattern(/^[a-zA-Z ]+$/)]],
                lastName: [this.user.lastName, [Validators.required, Validators.pattern(/^[a-zA-Z ]+$/)]],
                email: [this.user.email, [Validators.required, Validators.pattern(
                    /^[a-z0-9](\.?[a-z0-9]){1,}@hexagon.com$/)]],
                city: [this.user.city, [Validators.required, Validators.pattern(/^[a-zA-Z ]+$/)]],
                role: [this.selectedRole?.name, [Validators.required]]
            });
        } else {
            this.selectedRole = {} as RoleDto;
            this.userForm = this.fb.group({
                firstName: ['', [Validators.required, Validators.pattern(/^[a-zA-Z ]+$/)]],
                lastName: ['', [Validators.required, Validators.pattern(/^[a-zA-Z ]+$/)]],
                email: ['', [Validators.required, Validators.pattern(/^[a-z0-9](\.?[a-z0-9]){1,}@hexagon.com$/)]],
                city: ['', [Validators.required, Validators.pattern(/^[a-zA-Z ]+$/)]],
                role: ['', Validators.required]
            });
        }
    }

    public ngOnInit(): void {
    }

    public onSubmit(): void {
        this.progressIndicatingService.show({
            progress$: this.progress$.asObservable(),
            initial: {
                header: 'Saving User',
                details: 'Please wait...',
                done: false
            }
        });
        const roleId = this.roles.find(x => x.name === this.userForm.value.role)?.roleId;
        const roles = [{
            name: this.userForm.value.role,
            roleId: roleId
        }];
        if (this.user) {
            this.user.email = this.userForm.value.email;
            this.user.firstName = this.userForm.value.firstName;
            this.user.lastName = this.userForm.value.lastName;
            this.user.city = this.userForm.value.city;
            this.user.roles = roles;
            this.userService.updateUser(this.user).subscribe(
                (res) => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: res.responseMessage });
                    this.sendProgressDoneSignal();
                    this.ref.close(true);
                },
                (error) => {
                    this.sendProgressDoneSignal();
                    this.messageService.add({ severity: 'error', summary: 'Fail', detail: error.error.responseMessage });
                });
        } else {
            const user: UserCreateDto = {
                firstName: this.userForm.value.firstName,
                lastName: this.userForm.value.lastName,
                city: this.userForm.value.city,
                email: this.userForm.value.email,
                roles: roles
            };
            this.userService.addNewUser(user).subscribe(
                (res) => {
                    this.sendProgressDoneSignal();
                    this.ref.close(true);
                },
                (error) => {
                    this.sendProgressDoneSignal();
                    this.messageService.add({ severity: 'error', summary: 'Fail', detail: error.error.responseMessage });
                });
        }
    }

    public ngOnDestroy(): void {
        this.progress$.next({ header: '', done: true });
        this.progress$.complete();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private sendProgressDoneSignal(): void {
        this.progress$.next({
            header: '',
            done: true
        });
    }
}
